import type { OrganizationStatus } from '@zeus/index'
import type { OrgRow } from '~/gql/organizations/organizations'
import { useMutation } from '@apollo/client/react'
import { BillingMode, BillingTier } from '@zeus/index'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { GQL_SET_ORGANIZATION_BILLING_MODE, GQL_SET_ORGANIZATION_PLAN, GQL_UPDATE_ORGANIZATION } from '~/gql/organizations/organizations'
import { errorCodeKey } from '~/helpers/api/error-code'

export type OrganizationEdit = {
  name: string
  slug: string
  status: OrganizationStatus
  tier: BillingTier
  billingMode: BillingMode
  billingNote: string
}

type Callbacks = { onSuccess: () => void | Promise<unknown>, onClose: () => void }

/**
 * Enregistre la fiche organisation : identité et statut, puis la formule si elle
 * a changé (#165) — le mode géré (#349), puis le palier. Plusieurs mutations,
 * donc plusieurs échecs possibles : si seule la formule échoue, le reste est déjà
 * enregistré — la liste est rafraîchie et le message le dit, au lieu d'accuser
 * l'identifiant.
 *
 * Sortir du mode géré ramène au gratuit côté API : le palier choisi s'applique
 * ensuite, s'il n'est pas le gratuit.
 */
export function useOrganizationSave(org: OrgRow, { onSuccess, onClose }: Callbacks) {
  const { t } = useTranslation()
  const [updateOrganization, { loading: updating }] = useMutation(GQL_UPDATE_ORGANIZATION)
  const [setOrganizationPlan, { loading: settingPlan }] = useMutation(GQL_SET_ORGANIZATION_PLAN)
  const [setBillingMode, { loading: settingMode }] = useMutation(GQL_SET_ORGANIZATION_BILLING_MODE)

  const savePlan = async ({ tier, billingMode, billingNote }: OrganizationEdit) => {
    const managed = billingMode === BillingMode.MANAGED
    const wasManaged = org.billingMode === BillingMode.MANAGED
    const note = billingNote.trim()
    if (managed ? !wasManaged || note !== (org.billingNote ?? '') : wasManaged) {
      await setBillingMode({ variables: { orgId: org.id, input: { mode: billingMode, note: managed ? note : null } } })
    }
    const currentTier = wasManaged ? BillingTier.FREE : org.tier
    if (!managed && tier !== currentTier) {
      await setOrganizationPlan({ variables: { orgId: org.id, input: { tier } } })
    }
  }

  const save = async (edit: OrganizationEdit) => {
    const { name, slug, status } = edit
    try {
      await updateOrganization({ variables: { input: { id: org.id, name: name.trim(), slug: slug.trim(), status } } })
    } catch {
      toast.error(t('organization.edit.failed'))
      return
    }
    try {
      await savePlan(edit)
    } catch (error) {
      // Abonnée sur Stripe (#353) : le palier se change dans le portail, par l'admin.
      toast.error(t(errorCodeKey(error, { SUBSCRIPTION_RUNNING: 'organization.edit.planSubscribed' }, 'organization.edit.planFailed')))
      await onSuccess()
      return
    }
    toast.success(t('organization.edit.success'))
    await onSuccess()
    onClose()
  }

  return { save, loading: updating || settingPlan || settingMode }
}
